<template>
  <modal
    :id="modalId"
    :visible="!!formControl"
    :title="title"
    :button1-caption="saveLabel"
    :button2-caption="cancelLabel"
    :button2-visible="true"
    :button1-visible="true"
    :button1-disabled="formControl?.invalid"
    @confirmed="add"
    @cancelled="cancel">
    <template #modal-after-text>
      <div
        v-if="isEdit"
        class="alert alert-warning">
        <icon-warning />
        <p>
          {{ i18n.global.t('messages.editCustomFieldValueWarning', [organizationCustomField.typeDescription]) }}
        </p>
      </div>
      <text-form-field
        :id="id"
        :label="label"
        name="value"
        :control="formControl"
        :required="true"
        @value-changed="onValueChanged"
        @enter-pressed="add" />
    </template>
  </modal>
</template>

<script lang="ts" setup>
  import { capitalize, computed, onMounted, ref } from 'vue';

  import Modal from '@/common/components/controls/Modal.vue';
  import FormControl from '@/common/components/generic-form/form-controls/FormControl';
  import DefaultValidator from '@/common/components/generic-form/form-controls/validator/DefaultValidator';
  import TextFormField from '@/common/components/generic-form/TextFormField.vue';
  import { IconWarning } from '@/common/icons/monochrome';
  import { OrganizationCustomField } from '@/common/models';
  import OrganizationCustomFieldValue, {
    organizationCustomFieldValueLabel,
  } from '@/common/models/organization-custom-fields/OrganizationCustomFieldValue';
  import { mapOrgAppState } from '@/utils/StateMapping';
  import i18n from '@/vueI18n';

  interface IProps {
    organizationCustomField: OrganizationCustomField;
    customFieldValues: OrganizationCustomFieldValue[];
    customFieldValue?: OrganizationCustomFieldValue;
  }

  const id = 'add-organization-custom-field-value';
  const modalId = `${id}-modal`;
  const cancelLabel = capitalize(i18n.global.t('actions.cancel'));
  const label = capitalize(i18n.global.t('names.value', { n: 1 }));

  const { customFieldValue, customFieldValues, organizationCustomField } = defineProps<IProps>();
  const emits = defineEmits(['add', 'update', 'cancel']);

  const { organization } = mapOrgAppState();

  const organizationCustomFieldValue = ref<OrganizationCustomFieldValue>(null);
  const formControl = ref<FormControl<string>>(null);

  const customValue = computed<string>(() => formControl.value?.value);
  const isEdit = computed<boolean>(() => !!customFieldValue);
  const saveLabel = computed<string>(() =>
    isEdit.value ? capitalize(i18n.global.t('actions.update')) : capitalize(i18n.global.t('actions.add')),
  );
  const title = computed<string>(() => {
    const key = isEdit.value ? 'edit' : 'add';
    const action = capitalize(i18n.global.t(`actions.${key}`));
    return `${action} ${organizationCustomFieldValueLabel(false)}`;
  });
  const organizationCustomFieldId = computed<number>(() => organizationCustomField?.id ?? -1);

  function onValueChanged() {
    const { value } = formControl.value;
    formControl.value.update(value);
  }

  function add() {
    organizationCustomFieldValue.value.value = formControl.value.value;
    emits(isEdit.value ? 'update' : 'add', organizationCustomFieldValue.value);
  }

  function cancel() {
    emits('cancel');
  }

  function checkUniqueness() {
    return (
      !customFieldValues?.find((cfv) => lowercase(cfv.value) === lowercase(formControl.value?.value)) ||
      lowercase(customFieldValue?.value) === lowercase(formControl.value?.value)
    );
  }

  function uniqueValidator() {
    const isUnique = checkUniqueness();
    if (!isUnique) {
      return { unique: i18n.global.t('messages.alreadyExists', [customValue.value]) };
    }

    return null;
  }

  function lowercase(value: string) {
    if (!value) return '';
    return value.toLowerCase();
  }

  function initCustomFieldValue() {
    organizationCustomFieldValue.value =
      customFieldValue ??
      OrganizationCustomFieldValue.fromObject({
        organizationCustomValueId: -1,
        organizationId: organization.id,
        organizationCustomFieldId: organizationCustomFieldId.value,
        value: '',
      });
  }

  onMounted(() => {
    initCustomFieldValue();
    formControl.value = new FormControl(organizationCustomFieldValue.value.value, [
      DefaultValidator.required,
      uniqueValidator,
    ]);
  });
</script>

<style lang="scss">
  .alert {
    margin-bottom: 3rem;
  }
</style>
